/**
 * Dashboard Layout Component
 * Reusable layout for role-based dashboards
 * Includes sidebar, header, and main content area
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useDynamicMenu } from '../../hooks';
import { ConfirmDialog } from '../../components/dialogs';

const DashboardLayout = ({
  children,
  menuItems = [],
  sidebarOpen = true,
  onSidebarToggle,
  roleTitle = 'Dashboard',
  roleSubtitle = 'Portal',
  useDynamicMenuItems = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, getUserRole } = useAuth();
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  // Use dynamic menu if enabled, otherwise use provided menuItems
  const userRole = getUserRole();
  const { menuItems: dynamicMenuItems, isLoading: menuLoading, dashboardData } = useDynamicMenu(userRole);
  const finalMenuItems = useDynamicMenuItems ? dynamicMenuItems : menuItems;

  // Get user info from dashboard data or fallback to role title
  const userInfo = dashboardData?.user || null;
  const displayName = userInfo?.name || roleTitle.split(' ')[0];
  const displaySubtitle = userInfo?.role?.name || roleSubtitle;

  const handleLogoutClick = () => {
    setShowLogoutDialog(true);
  };

  const handleLogoutConfirm = async () => {
    setShowLogoutDialog(false);
    await logout();
    navigate('/');
  };

  const handleLogoutCancel = () => {
    setShowLogoutDialog(false);
  };

  // Determine active tab from current route
  const getActiveTabFromRoute = () => {
    const pathSegments = location.pathname.split('/');
    const lastSegment = pathSegments[pathSegments.length - 1];

    // Handle index routes (empty last segment)
    if (!lastSegment || lastSegment === 'cvo-dashboard' || lastSegment === 'authority-dashboard') {
      return 'inbox'; // Default to inbox
    }

    return lastSegment;
  };

  const activeTab = getActiveTabFromRoute();

  // Get active menu item details
  const activeMenuItem = finalMenuItems.find(item => item.id === activeTab) || finalMenuItems[0];

  // Handle navigation to different tabs
  const handleTabNavigation = (tabId) => {
    const basePath = userRole === 'cvo' ? '/cvo-dashboard' : '/authority-dashboard';
    navigate(`${basePath}/${tabId}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex overflow-x-hidden dashboard-container">
      {/* Sidebar */}
      <div className={`${sidebarOpen ? 'w-80' : 'w-16'} bg-primary-500 text-white transition-all duration-300 flex-shrink-0 flex flex-col h-screen sticky top-0`}>
        {/* Fixed Top Section */}
        <div className="p-4 flex-shrink-0">
          {/* Logo and Title */}
          <div className={`flex items-center mb-2 ${sidebarOpen ? '' : 'justify-center'}`}>
            <img
              src={`${process.env.PUBLIC_URL}/logo192.png`}
              alt="SJVN Limited Logo"
              className={`w-10 h-12 ${sidebarOpen ? 'mr-3' : ''}`}
              role="img"
              aria-label="SJVN Limited Logo"
            />
            {sidebarOpen && (
              <div>
                <h1 className="text-lg font-bold">{roleTitle}</h1>
                <p className="text-xs text-primary-100">{roleSubtitle}</p>
              </div>
            )}
          </div>
        </div>

        {/* Scrollable Navigation Section */}
        <div className={`flex-1 overflow-y-auto ${sidebarOpen ? 'px-4' : 'px-2'}`}>
          <nav className="space-y-2 pb-4" role="navigation" aria-label="Main navigation">
            {finalMenuItems.map((option) => (
              <button
                key={option.id}
                onClick={() => handleTabNavigation(option.id)}
                className={`w-full flex items-center ${sidebarOpen ? 'p-3' : 'p-2 justify-center'} rounded-lg transition-all duration-300 group ${activeTab === option.id
                    ? 'bg-primary-600 text-white shadow-lg'
                    : 'text-primary-100 hover:bg-primary-600 hover:text-white'
                  }`}
                aria-current={activeTab === option.id ? 'page' : undefined}
                aria-label={option.description}
              >
                <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={option.icon} />
                </svg>
                {sidebarOpen && (
                  <div className="ml-3 text-left flex-1">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium">{option.title}</div>
                      {/* Dynamic notification badges */}
                      {option.notificationCount > 0 && (
                        <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                          {option.notificationCount > 99 ? '99+' : option.notificationCount}
                        </span>
                      )}
                      {option.complaintCount > 0 && option.id !== 'inbox' && (
                        <span className="bg-blue-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                          {option.complaintCount > 99 ? '99+' : option.complaintCount}
                        </span>
                      )}
                      {option.officerCount > 0 && (
                        <span className="bg-green-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                          {option.officerCount > 99 ? '99+' : option.officerCount}
                        </span>
                      )}
                    </div>
                    <div className="text-xs opacity-75">{option.description}</div>
                  </div>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10" role="banner">
          <div className="px-6 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center space-x-4">
                {/* Sidebar Toggle */}
                <button
                  onClick={onSidebarToggle}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-300"
                  aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
                >
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={sidebarOpen ? "M11 19l-7-7 7-7m8 14l-7-7 7-7" : "M13 5l7 7-7 7M5 5l7 7-7 7"} />
                  </svg>
                </button>

                {/* Current Tab Display */}
                <div className="flex flex-col">
                  <h1 className="text-xl font-bold text-gray-900">
                    {activeMenuItem?.title || 'Dashboard'}
                  </h1>
                  <p className="text-sm text-gray-500">
                    {activeMenuItem?.description || 'Manage vigilance operations'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-4 gap-0">
                <div className="text-right">
                  <p className="text-sm font-medium text-gray-900">Welcome, {displayName}</p>
                  <p className="text-xs text-gray-500">{displaySubtitle}</p>
                </div>
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <button
                  onClick={handleLogoutClick}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors duration-300"
                  aria-label="Logout"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main
          className="flex-1 p-6 overflow-hidden min-w-0 main-content"
          role="main"
          style={{ maxWidth: sidebarOpen ? 'calc(100vw - 320px)' : 'calc(100vw - 64px)' }}
        >
          {children}
        </main>
      </div>

      {/* Logout Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showLogoutDialog}
        title="Confirm Logout"
        message="Are you sure you want to logout? You will need to login again to access the dashboard."
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
        confirmText="Logout"
        cancelText="Cancel"
      />
    </div>
  );
};

export default DashboardLayout;

